<script setup lang="ts">
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import { formatDateTime } from '@/utils/formatters'
import type { IImportActiveItem } from '../types/IImportActive'

const props = withDefaults(defineProps<{
  activeTasks?: IImportActiveItem[]
  importType?: string
}>(), {
  activeTasks: () => [],
  importType: '',
})

const emit = defineEmits<{
  'cancel-task': [id: number]
  'new-import': []
}>()

// Сортировка задач: in_progress всегда наверху, далее по очереди и времени
const sortedTasks = computed(() => {
  return [...props.activeTasks].sort((a, b) => {
    if (a.status === 'in_progress' && b.status !== 'in_progress') return -1
    if (b.status === 'in_progress' && a.status !== 'in_progress') return 1

    const posA = a.queue_position ?? Infinity
    const posB = b.queue_position ?? Infinity
    if (posA !== posB) return posA - posB

    const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
    const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
    return timeA - timeB
  })
})

// Очередь (Режим 2): если задач больше одной или есть задачи со статусом pending (в очереди)
const hasQueue = computed(() => {
  const tasks = sortedTasks.value
  if (tasks.length > 1) return true
  return tasks.some(t => t.status === 'pending')
})

// Одиночная активная задача
const singleTask = computed(() => sortedTasks.value[0] || null)

const singleStartedLabel = computed(() => {
  if (!singleTask.value?.created_at) return '—'
  return formatDateTime(singleTask.value.created_at) || '—'
})

const singlePercentLabel = computed(() => `${Math.round(singleTask.value?.percent || 0)}%`)

function taskDateLabel(dateStr?: string | null) {
  if (!dateStr) return '—'
  return formatDateTime(dateStr) || '—'
}

function isPending(status: string) {
  return status === 'pending'
}

function isCancelled(status: string) {
  return status === 'cancelled'
}

function isInProgress(status: string) {
  return status === 'in_progress'
}
</script>

<template>
  <div class="import-active-container d-flex flex-column ga-6">
    <!-- Вариант 1: Одиночный импорт без очереди -->
    <div v-if="!hasQueue" class="import-single-flow d-flex flex-column ga-4">
      <div class="d-flex flex-column ga-2">
        <span class="text-h5 import-active-title">Идет процесс импорта</span>
        <div>
          <span class="body-1-500 import-active-label">Запущен: </span>
          <span class="body-1 import-active-muted">{{ singleStartedLabel }}</span>
        </div>
      </div>

      <div class="import-active-card d-flex flex-column align-center ga-4 pa-6 bg-surface">
        <AnimatedLogoLoader :size="48" />
        <span class="body-1 import-active-muted text-center text-break">{{ singleTask?.file_name || '—' }}</span>
        <span class="text-h5 import-active-title">{{ singlePercentLabel }}</span>
        <VProgressLinear
          class="w-100"
          :model-value="singleTask?.percent || 0"
          color="primary"
          height="8"
          rounded
        />
        <VBtn
          v-if="singleTask && singleTask.status !== 'cancelled'"
          variant="outlined"
          color="error"
          @click="emit('cancel-task', singleTask.id)"
        >
          Отменить импортирование
        </VBtn>
      </div>

      <div>
        <VBtn
          variant="outlined"
          color="secondary"
          @click="emit('new-import')"
        >
          Перейти к новому импорту
        </VBtn>
      </div>
    </div>

    <!-- Вариант 2: Импорт с очередью (по макету) -->
    <div v-else class="import-queue-flow d-flex flex-column ga-4">
      <div class="d-flex flex-column ga-1">
        <span class="text-h5 import-queue-title">Импорт ожидает своей очереди</span>
        <span class="body-1 import-queue-subtitle">
          Сейчас выполняется другой импорт. Ваш импорт начнётся автоматически, когда освободится очередь.
        </span>
      </div>

      <div class="import-queue-card">
        <VTable class="custom-data-table import-queue-table">
          <thead>
            <tr>
              <th class="col-file">ФАЙЛ</th>
              <th class="col-date">ВРЕМЯ ЗАГРУЗКИ</th>
              <th class="col-progress">ПРОГРЕСС</th>
              <th class="col-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in sortedTasks" :key="task.id">
              <td class="col-file text-body-1 font-weight-medium">
                {{ task.file_name }}
              </td>
              <td class="col-date text-body-1">
                {{ taskDateLabel(task.created_at) }}
              </td>
              <td class="col-progress">
                <div v-if="isInProgress(task.status)" class="d-flex align-center ga-3 progress-wrap">
                  <span class="body-2 font-weight-medium text-no-wrap percent-text">
                    {{ Math.round(task.percent || 0) }}%
                  </span>
                  <VProgressLinear
                    :model-value="task.percent || 0"
                    color="primary"
                    height="6"
                    rounded
                    class="flex-grow-1"
                  />
                </div>
                <VChip
                  v-else-if="isPending(task.status)"
                  size="small"
                  color="secondary"
                  variant="tonal"
                  class="status-chip"
                >
                  Ожидает
                </VChip>
                <VChip
                  v-else-if="isCancelled(task.status)"
                  size="small"
                  color="error"
                  variant="tonal"
                  class="status-chip chip-cancelled"
                >
                  Отменен
                </VChip>
                <span v-else class="body-2">{{ task.status_display }}</span>
              </td>
              <td class="col-action text-end">
                <VBtn
                  v-if="!isCancelled(task.status)"
                  variant="outlined"
                  color="error"
                  size="small"
                  class="cancel-btn"
                  @click="emit('cancel-task', task.id)"
                >
                  Отменить
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>
      </div>

      <div>
        <VBtn
          variant="outlined"
          color="secondary"
          @click="emit('new-import')"
        >
          Перейти к новому импорту
        </VBtn>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.import-active-container {
  max-width: 900px;
}

.import-single-flow {
  max-width: 700px;
}

.import-active-title,
.import-queue-title {
  color: rgb(var(--v-theme-text-primary)) !important;
  font-feature-settings: 'liga' off, 'clig' off;
}

.import-active-label {
  color: rgb(var(--v-theme-text-primary));
  font-feature-settings: 'liga' off, 'clig' off;
}

.import-active-muted,
.import-queue-subtitle {
  color: rgb(var(--v-theme-text-secondary)) !important;
  font-feature-settings: 'liga' off, 'clig' off;
}

.import-active-card {
  border: 1px solid rgba(var(--v-theme-on-surface), var(--v-border-opacity));
  border-radius: 8px;
}

.import-queue-card {
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), var(--v-border-opacity));
  border-radius: 8px;
  overflow: hidden;
}

:deep(.import-queue-table) {
  width: 100%;
  border-collapse: collapse;

  th {
    background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    letter-spacing: 0 !important;
    line-height: 22px !important;
    padding-inline: 1rem;
    position: relative;
    text-align: start;
    white-space: nowrap;

    &:not(:last-child)::after {
      background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
      content: '';
      height: 14px;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 2px;
    }
  }

  td {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    padding-inline: 1rem;
    vertical-align: middle;
  }

  .col-file {
    min-width: 220px;
  }

  .col-date {
    min-width: 160px;
  }

  .col-progress {
    min-width: 200px;
  }

  .col-action {
    width: 110px;
    text-align: end;
  }

  .progress-wrap {
    max-width: 200px;
  }

  .percent-text {
    width: 38px;
  }

  .status-chip {
    font-weight: 500;
    border-radius: 12px;
  }

  .cancel-btn {
    border-radius: 6px;
    text-transform: none;
    font-weight: 400;
  }
}
</style>

